import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { isValidSlug, slugify } from "../../../shared/slug";
import { api } from "../../api";
import { IconCopy } from "../../icons";
import { useShell } from "../AdminShell";
import { TabFooter, useEditor } from "../DemoEditor";

export default function BasicsTab() {
  const { bundle, setBundle, setDirty } = useEditor();
  const { refreshNav } = useShell();
  const nav = useNavigate();
  const d = bundle.demo;
  const [f, setF] = useState({ title: d.title, slug: d.slug, ctaLabel: d.ctaLabel, ctaUrl: d.ctaUrl, transition: d.transition,
    leadGate: d.leadGate, leadGateStep: d.leadGateStep, leadAskName: d.leadAskName, leadAskCompany: d.leadAskCompany,
    leadSkippable: d.leadSkippable, leadTitle: d.leadTitle, leadWebhookUrl: d.leadWebhookUrl,
    audioEnabled: d.audioEnabled ?? true, closedCaptionsEnabled: d.closedCaptionsEnabled ?? true,
  });
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [duplicating, setDuplicating] = useState(false);
  const set = (p: Partial<typeof f>) => { setF({ ...f, ...p }); setDirty(true); setMsg(null); };

  const save = async () => {
    try {
      setBundle(await api.updateDemo(d.id, f));
      setDirty(false);
      setMsg({ ok: true, text: "Saved" });
    } catch (e: any) { setMsg({ ok: false, text: e.message }); }
  };
  const remove = async () => {
    if (!confirm(`Delete "${d.title}"? Its screens, survey and analytics are removed and the public link stops working.`)) return;
    await api.deleteDemo(d.id);
    setDirty(false);
    nav(`/admin/products/${bundle.product.id}`);
  };
  const duplicate = async () => {
    try {
      setDuplicating(true);
      const cloned = await api.duplicateDemo(d.id);
      await refreshNav();
      setDirty(false);
      nav(`/admin/demos/${cloned.demo.id}/basics`);
    } catch (e: any) {
      setMsg({ ok: false, text: e.message });
      setDuplicating(false);
    }
  };
  const valid = f.title.trim() && isValidSlug(f.slug) && (!f.ctaUrl || /^https?:\/\//.test(f.ctaUrl)) && (!f.leadWebhookUrl || /^https?:\/\//.test(f.leadWebhookUrl));

  return (
    <div className="stack" style={{ maxWidth: 760 }}>
      <div className="panel stack">
        <div className="grid-2">
          <label className="field"><span>Demo title</span><input type="text" value={f.title} onChange={(e) => set({ title: e.target.value })} /></label>
          <label className="field"><span>URL name <span className="hint">/{bundle.product.slug}/{f.slug}</span></span>
            <input type="text" value={f.slug} onChange={(e) => set({ slug: slugify(e.target.value) })} />
          </label>
        </div>
        <h3>End-of-demo button</h3>
        <p className="small muted">Shown when the viewer finishes. Leave the link empty to show only "Watch again".</p>
        <div className="grid-2">
          <label className="field"><span>Button text <span className="hint">{"{{variables}}"} work here</span></span><input type="text" placeholder="Talk to an engineer" value={f.ctaLabel} onChange={(e) => set({ ctaLabel: e.target.value })} /></label>
          <label className="field"><span>Link</span><input type="url" placeholder="https://calendly.com/…" value={f.ctaUrl} onChange={(e) => set({ ctaUrl: e.target.value.trim() })} /></label>
        </div>
        <h3>Screen transition</h3>
        <p className="small muted">How the player moves from one screen to the next.</p>
        <div className="path-tabs" role="radiogroup" aria-label="Screen transition">
          {([["none", "Cut"], ["fade", "Fade"]] as const).map(([k, label]) => (
            <button key={k} type="button" role="radio" aria-checked={f.transition === k} className={f.transition === k ? "on" : ""} onClick={() => set({ transition: k })}>{label}</button>
          ))}
        </div>
        <div className="divider" />
        <h3>Voiceover</h3>
        <p className="small muted">Control audio playback and closed captions (CC).</p>
        <div className="row" style={{ gap: 20 }}>
          <label className="check"><input type="checkbox" checked={f.audioEnabled} onChange={(e) => set({ audioEnabled: e.target.checked })} />Play audio during preview/publish</label>
          <label className="check"><input type="checkbox" checked={f.closedCaptionsEnabled} onChange={(e) => set({ closedCaptionsEnabled: e.target.checked })} />Show Closed Captions (CC)</label>
        </div>
        <div className="divider" />
        <h3>Lead capture</h3>
        <p className="small muted">Ask for a work email so you know who watched. Leads show up in Analytics, with a CSV export.</p>
        <div className="path-tabs" role="radiogroup" aria-label="When to ask">
          {([["off", "Off"], ["start", "Before the tour"], ["step", "Before a screen"], ["end", "Before the end card"]] as const).map(([k, label]) => (
            <button key={k} type="button" role="radio" aria-checked={f.leadGate === k} className={f.leadGate === k ? "on" : ""} onClick={() => set({ leadGate: k })}>{label}</button>
          ))}
        </div>
        {f.leadGate !== "off" && (
          <div className="stack">
            {f.leadGate === "step" && (
              <label className="field" style={{ maxWidth: 220 }}><span>Show before screen</span>
                <input type="number" min={1} max={500} value={f.leadGateStep} onChange={(e) => set({ leadGateStep: Math.max(1, Math.min(500, Number(e.target.value) || 1)) })} />
              </label>
            )}
            <label className="field"><span>Form heading <span className="hint">Optional. {"{{variables}}"} work here</span></span>
              <input type="text" placeholder="Where should we send a copy of this tour?" value={f.leadTitle} onChange={(e) => set({ leadTitle: e.target.value })} />
            </label>
            <div className="row" style={{ gap: 20 }}>
              <label className="check"><input type="checkbox" checked disabled />Work email (always)</label>
              <label className="check"><input type="checkbox" checked={f.leadAskName} onChange={(e) => set({ leadAskName: e.target.checked })} />Name</label>
              <label className="check"><input type="checkbox" checked={f.leadAskCompany} onChange={(e) => set({ leadAskCompany: e.target.checked })} />Company</label>
              <label className="check"><input type="checkbox" checked={f.leadSkippable} onChange={(e) => set({ leadSkippable: e.target.checked })} />Let viewers skip it</label>
            </div>
            <label className="field"><span>Send leads to a webhook <span className="hint">Optional. Zapier, Make or your CRM; each lead is POSTed as JSON</span></span>
              <input type="url" className="mono" placeholder="https://hooks.zapier.com/…" value={f.leadWebhookUrl} onChange={(e) => set({ leadWebhookUrl: e.target.value.trim() })} />
            </label>
          </div>
        )}
        {msg && <div className={msg.ok ? "ok-box" : "error-box"}>{msg.text}</div>}
        <div className="row">
          <button className="btn btn-primary" disabled={!valid} onClick={save}>Save changes</button>
          <button type="button" className="btn btn-secondary" disabled={duplicating} onClick={duplicate}>
            <IconCopy />{duplicating ? "Duplicating…" : "Duplicate demo"}
          </button>
          <span className="spacer" />
          <button className="btn btn-danger" onClick={remove}>Delete demo</button>
        </div>
      </div>
      <TabFooter next="outcomes" />
    </div>
  );
}
